import { openDB, type IDBPDatabase } from 'idb';
import type { ResidencyYear, CRARoomMethod } from '../engine/types';

export interface SettingsRecord {
  birth_year: number | null;
  residency_year: ResidencyYear | null;
  cra_room_initial_cents: string | null;
  cra_room_method: CRARoomMethod | null;
  cra_verified_at_iso: string | null;
  contributions_ytd_cents: string;
  withdrawals_ytd_cents: string;
  wizard_completed_at: string | null;
  disclaimer_accepted_at: string | null;
  last_backup_export_at: string | null;
  contributions_since_last_backup: number;
}

export interface ContributionRecord {
  id?: number;
  date: string;
  amount_cents: string;
  institution?: string;
  created_at: string;
}

export interface WithdrawalRecord {
  id?: number;
  date: string;
  amount_cents: string;
  institution?: string;
  created_at: string;
}

export interface ConstantsCacheRecord {
  tax_year: string;
  version: string;
  sha256: string;
  verified_at: string;
  applied_at: string | null;
  payload: unknown;
}

const DB_NAME = 'HBFTFSAStore';
const DB_VERSION = 1;
const SETTINGS_KEY = 'main';

export type HBFDatabase = IDBPDatabase;

export async function openDb(): Promise<HBFDatabase> {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings');
      }
      if (!db.objectStoreNames.contains('contributions')) {
        const s = db.createObjectStore('contributions', { keyPath: 'id', autoIncrement: true });
        s.createIndex('by_date', 'date');
      }
      if (!db.objectStoreNames.contains('withdrawals')) {
        const s = db.createObjectStore('withdrawals', { keyPath: 'id', autoIncrement: true });
        s.createIndex('by_date', 'date');
      }
      if (!db.objectStoreNames.contains('constants_cache')) {
        db.createObjectStore('constants_cache', { keyPath: 'tax_year' });
      }
    },
  });
}

export async function getSettings(db: HBFDatabase): Promise<SettingsRecord | null> {
  return (await db.get('settings', SETTINGS_KEY)) ?? null;
}

export async function putSettings(db: HBFDatabase, settings: SettingsRecord): Promise<void> {
  await db.put('settings', settings, SETTINGS_KEY);
}

export async function addContribution(
  db: HBFDatabase,
  record: Omit<ContributionRecord, 'id'>
): Promise<number> {
  return db.add('contributions', record) as Promise<number>;
}

export async function getAllContributions(db: HBFDatabase): Promise<Required<ContributionRecord>[]> {
  return db.getAll('contributions') as Promise<Required<ContributionRecord>[]>;
}

export async function deleteContribution(db: HBFDatabase, id: number): Promise<void> {
  await db.delete('contributions', id);
}

/**
 * Apply a partial patch to a contribution record in IDB. No-op if the id is
 * missing. Setting a field to `undefined` removes it from the stored record
 * (matches the in-memory LogEntryPatch semantics for `institution`).
 */
export async function updateContribution(
  db: HBFDatabase,
  id: number,
  patch: Partial<Pick<ContributionRecord, 'amount_cents' | 'date'>> & { institution?: string | undefined }
): Promise<void> {
  const existing = (await db.get('contributions', id)) as Required<ContributionRecord> | undefined;
  if (!existing) return;
  const next: Required<ContributionRecord> = { ...existing };
  if (patch.amount_cents !== undefined) next.amount_cents = patch.amount_cents;
  if (patch.date !== undefined) next.date = patch.date;
  if ('institution' in patch) {
    if (patch.institution === undefined) {
      delete (next as { institution?: string }).institution;
    } else {
      next.institution = patch.institution;
    }
  }
  await db.put('contributions', next);
}

export async function addWithdrawal(
  db: HBFDatabase,
  record: Omit<WithdrawalRecord, 'id'>
): Promise<number> {
  return db.add('withdrawals', record) as Promise<number>;
}

export async function getAllWithdrawals(db: HBFDatabase): Promise<Required<WithdrawalRecord>[]> {
  return db.getAll('withdrawals') as Promise<Required<WithdrawalRecord>[]>;
}

export async function deleteWithdrawal(db: HBFDatabase, id: number): Promise<void> {
  await db.delete('withdrawals', id);
}

export async function updateWithdrawal(
  db: HBFDatabase,
  id: number,
  patch: Partial<Pick<WithdrawalRecord, 'amount_cents' | 'date'>> & { institution?: string | undefined }
): Promise<void> {
  const existing = (await db.get('withdrawals', id)) as Required<WithdrawalRecord> | undefined;
  if (!existing) return;
  const next: Required<WithdrawalRecord> = { ...existing };
  if (patch.amount_cents !== undefined) next.amount_cents = patch.amount_cents;
  if (patch.date !== undefined) next.date = patch.date;
  if ('institution' in patch) {
    if (patch.institution === undefined) {
      delete (next as { institution?: string }).institution;
    } else {
      next.institution = patch.institution;
    }
  }
  await db.put('withdrawals', next);
}

export async function getCachedConstants(
  db: HBFDatabase,
  taxYear: string
): Promise<ConstantsCacheRecord | null> {
  return (await db.get('constants_cache', taxYear)) ?? null;
}

export async function putCachedConstants(
  db: HBFDatabase,
  record: ConstantsCacheRecord
): Promise<void> {
  await db.put('constants_cache', record);
}

export async function clearAllData(db: HBFDatabase): Promise<void> {
  const tx = db.transaction(['settings', 'contributions', 'withdrawals', 'constants_cache'], 'readwrite');
  await Promise.all([
    tx.objectStore('settings').clear(),
    tx.objectStore('contributions').clear(),
    tx.objectStore('withdrawals').clear(),
    tx.objectStore('constants_cache').clear(),
    tx.done,
  ]);
}
